(() => {
  function logoPixelAlpha(r, g, b, a) {
    if (a <= 12) return 0;
    const whiteness = Math.min(r, g, b);
    const threshold = 150;
    if (whiteness <= threshold) return 0;
    const brightnessAlpha = (whiteness - threshold) / (255 - threshold);
    const sourceAlpha = a / 255;
    return Math.max(0, Math.min(1, Math.pow(brightnessAlpha, 0.9) * sourceAlpha));
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { logoPixelAlpha };
  }

  if (typeof document === 'undefined') return;

  const WAIT_MS = 2600;
  const VAPOR_MS = 1400;
  const REFORM_MS = 900;

  const clamp01 = (value) => Math.max(0, Math.min(1, value));
  const easeOutCubic = (value) => 1 - Math.pow(1 - clamp01(value), 3);

  function init() {
    const hero = document.querySelector('.etrnl-hero');
    const sourceLogo = hero && hero.querySelector('.etrnl-hero-logo');
    if (!hero || !sourceLogo || sourceLogo.dataset.vaporReady === 'true') return;

    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const logoUrl = sourceLogo.currentSrc || sourceLogo.src;
    if (!logoUrl) return;

    const logoImage = new Image();
    logoImage.crossOrigin = 'anonymous';
    logoImage.decoding = 'async';

    logoImage.addEventListener('error', () => {
      sourceLogo.style.opacity = '';
    }, { once: true });

    logoImage.addEventListener('load', () => {
      const wrap = document.createElement('span');
      wrap.className = 'etrnl-logo-vapor-wrap';
      sourceLogo.parentNode.insertBefore(wrap, sourceLogo);
      wrap.appendChild(sourceLogo);

      const canvas = document.createElement('canvas');
      canvas.className = 'etrnl-logo-vapor-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      wrap.appendChild(canvas);

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        canvas.remove();
        wrap.replaceWith(sourceLogo);
        return;
      }

      let dpr = 1;
      let particles = [];
      let phase = 'waiting';
      let phaseStarted = performance.now();
      let raf = 0;
      let visible = true;
      let resizeTimer = 0;

      function buildParticles() {
        const rect = sourceLogo.getBoundingClientRect();
        const width = Math.max(1, Math.round(rect.width));
        const height = Math.max(1, Math.round(rect.height));
        if (width < 2 || height < 2) return false;

        dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const sampler = document.createElement('canvas');
        sampler.width = width;
        sampler.height = height;
        const sampleCtx = sampler.getContext('2d', { willReadFrequently: true });
        if (!sampleCtx) return false;

        sampleCtx.clearRect(0, 0, width, height);
        sampleCtx.imageSmoothingEnabled = true;
        sampleCtx.imageSmoothingQuality = 'high';
        sampleCtx.drawImage(logoImage, 0, 0, width, height);

        let imageData;
        try {
          imageData = sampleCtx.getImageData(0, 0, width, height).data;
        } catch (error) {
          return false;
        }

        const step = width >= 280 ? 3 : 2;
        const next = [];

        for (let y = 0; y < height; y += step) {
          for (let x = 0; x < width; x += step) {
            const index = (y * width + x) * 4;
            const alpha = logoPixelAlpha(
              imageData[index],
              imageData[index + 1],
              imageData[index + 2],
              imageData[index + 3]
            );
            if (alpha <= 0) continue;

            const normalizedX = width > 1 ? x / (width - 1) : 0;
            next.push({
              ox: x,
              oy: y,
              alpha,
              trigger: clamp01(normalizedX * 0.52 + Math.random() * 0.12),
              dx: 10 + Math.random() * 24,
              dy: -12 - Math.random() * 34,
              size: Math.max(1.25, step * (0.62 + Math.random() * 0.26))
            });
          }
        }

        particles = next;
        return particles.length > 0;
      }

      function clearCanvas() {
        const width = canvas.width / dpr;
        const height = canvas.height / dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);
        ctx.globalAlpha = 1;
      }

      function drawParticle(particle, x, y, alpha) {
        if (alpha <= 0.01) return;
        ctx.globalAlpha = clamp01(alpha);
        ctx.fillStyle = '#fff';
        ctx.fillRect(x, y, particle.size, particle.size);
      }

      function drawVapor(progress) {
        clearCanvas();
        particles.forEach((particle) => {
          const local = clamp01((progress - particle.trigger) / Math.max(0.01, 1 - particle.trigger));
          const eased = easeOutCubic(local);
          drawParticle(
            particle,
            particle.ox + particle.dx * eased,
            particle.oy + particle.dy * eased,
            particle.alpha * (1 - eased)
          );
        });
        sourceLogo.style.opacity = String(1 - easeOutCubic(progress));
        ctx.globalAlpha = 1;
      }

      function drawReform(progress) {
        clearCanvas();
        const eased = easeOutCubic(progress);
        particles.forEach((particle) => {
          const sparkle = Math.sin(Math.PI * progress) * 0.42;
          drawParticle(
            particle,
            particle.ox + particle.dx * (1 - eased),
            particle.oy + particle.dy * (1 - eased),
            particle.alpha * sparkle
          );
        });
        sourceLogo.style.opacity = String(eased);
        ctx.globalAlpha = 1;
      }

      function enter(nextPhase, now) {
        phase = nextPhase;
        phaseStarted = now;
        if (nextPhase === 'waiting') {
          sourceLogo.style.opacity = '1';
          clearCanvas();
        }
      }

      function tick(now) {
        raf = requestAnimationFrame(tick);
        if (!visible || !particles.length) return;

        const elapsed = now - phaseStarted;
        if (phase === 'waiting') {
          if (elapsed >= WAIT_MS) enter('vaporizing', now);
          return;
        }

        if (phase === 'vaporizing') {
          const progress = clamp01(elapsed / VAPOR_MS);
          drawVapor(progress);
          if (progress >= 1) enter('reforming', now);
          return;
        }

        const progress = clamp01(elapsed / REFORM_MS);
        drawReform(progress);
        if (progress >= 1) enter('waiting', now);
      }

      function rebuild() {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          sourceLogo.style.opacity = '1';
          clearCanvas();
          if (buildParticles()) enter('waiting', performance.now());
        }, 120);
      }

      if (!buildParticles()) {
        canvas.remove();
        wrap.replaceWith(sourceLogo);
        sourceLogo.style.opacity = '';
        return;
      }

      sourceLogo.dataset.vaporReady = 'true';
      sourceLogo.style.opacity = '1';

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(rebuild);
        observer.observe(sourceLogo);
      } else {
        window.addEventListener('resize', rebuild, { passive: true });
      }

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          visible = Boolean(entries[0] && entries[0].isIntersecting);
          if (visible) enter('waiting', performance.now());
        }, { threshold: 0, rootMargin: '80px' });
        observer.observe(hero);
      }

      raf = requestAnimationFrame(tick);

      window.addEventListener('pagehide', () => {
        if (raf) cancelAnimationFrame(raf);
        sourceLogo.style.opacity = '1';
      }, { once: true });
    }, { once: true });

    logoImage.src = logoUrl;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
